import moment from 'moment-timezone';
import { SaveAppointment, SaveAppointmentDetails } from 'types/Appointment';
import { BookingChannel } from 'types/Booking';
import { CustomerAddress } from 'types/Customer';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { OnlineBookingSessionItem } from 'types/OnlineBooking';
import {
    formatCustomerAddress,
    resolveWalkingServiceConfig,
    SESSION_AREA_COPY
} from 'types/Walking';
import { getServiceDurationByPet } from 'utils/serviceUtils';
import { v4 as uuid } from 'uuid';

export interface WalkingSaveAppointmentContext {
    marketplaceAddressId: number;
    customerId: number;
    notes?: string;
    status: number;
    timeZone: string;
    orderItemPrice?: number;
    uuid?: string;
    customerAddress?: CustomerAddress;
}

const buildWalkingBookedAt = (
    item: Pick<OnlineBookingSessionItem, 'date' | 'time'>,
    timeZone: string
) => {
    if (item.time?.datetime) {
        return moment.parseZone(item.time.datetime);
    }

    const dateStr = item.date?.format('YYYY-MM-DD') ?? moment.tz(timeZone).format('YYYY-MM-DD');

    return moment
        .tz(dateStr, 'YYYY-MM-DD', timeZone)
        .hour(item.time?.hour ?? 0)
        .minute(item.time?.minute ?? 0)
        .second(0)
        .millisecond(0);
};

export const buildWalkingSaveAppointment = (
    item: OnlineBookingSessionItem,
    ctx: WalkingSaveAppointmentContext
): SaveAppointment => {
    const { label } = SESSION_AREA_COPY[item.businessArea];

    if (!item.service) {
        throw new Error(`${label} service not selected`);
    }

    if (!item.date) {
        throw new Error(`${label} date not selected`);
    }

    if (!item.time) {
        throw new Error(`${label} time not selected`);
    }

    const staffId = item.staff?.id;
    const { inHomeService, bookingType } = resolveWalkingServiceConfig(item.service);
    const duration = getServiceDurationByPet(item.service, item.pet.type, item.pet.size);
    const sessionDetails = {
        type: inHomeService ? ('in_home' as const) : ('brick_and_mortar' as const),
        address:
            inHomeService && ctx.customerAddress
                ? formatCustomerAddress(ctx.customerAddress)
                : null,
        duration: duration === false ? 0 : duration
    };
    const details: SaveAppointmentDetails =
        item.businessArea === MarketplaceServiceCategory.TRAINING
            ? { training: { ...sessionDetails, sessionType: bookingType } }
            : { walking: sessionDetails };
    const bookedAt = buildWalkingBookedAt(item, ctx.timeZone);

    return {
        uuid: ctx.uuid ?? uuid(),
        petId: item.pet.id ?? 0,
        serviceId: item.service.id,
        addressId: ctx.marketplaceAddressId,
        customerId: ctx.customerId,
        staffId,
        createdBy: staffId,
        notes: ctx.notes,
        channel: BookingChannel.ONLINE_BOOKING,
        status: ctx.status,
        orderItemPrice: ctx.orderItemPrice,
        bookedAt,
        // Pets on one Private + At Facility service go back to back, so each sends its own end.
        bookingEndAt: duration ? bookedAt.clone().add(duration, 'minutes') : undefined,
        details
    };
};
